// packages/ui/src/CheckboxField.tsx

import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Checkbox } from './Checkbox';

export interface CheckboxFieldProps {
  id?: string;
  name?: string;
  /** The controlled checked state of the checkbox. */
  checked: boolean;
  /** Callback fired when the state is changed. Receives the new boolean state directly. */
  onChange: (checked: boolean) => void;
  /** The text label for the checkbox. */
  label: string;
  labelKey?: string;
  /** Optional descriptive text that appears below the label. */
  description?: string;
  descriptionKey?: string;
  disabled?: boolean;
  /** Class name for the wrapper div. */
  className?: string;
  labelClassName?: string;
  descriptionClassName?: string;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  id,
  name,
  checked,
  onChange,
  label,
  labelKey,
  description,
  descriptionKey,
  disabled = false,
  className,
  labelClassName,
  descriptionClassName,
}) => {
  const generatedId = useId();
  const uniqueId = id || generatedId;

  const { t } = useTranslation();

  const resolvedLabel = labelKey ? t(labelKey) : label;
  const resolvedDescription = descriptionKey ? t(descriptionKey) : description;

  return (
    <div className={clsx('flex items-start gap-3', className)}>
      <Checkbox
        id={uniqueId}
        name={name}
        checked={checked}
        // Radix's onCheckedChange can return `boolean` or `'indeterminate'`.
        // Our onChange prop expects a boolean, so this works perfectly.
        onCheckedChange={onChange}
        disabled={disabled}
        aria-describedby={resolvedDescription ? `${uniqueId}-description` : undefined}
      />
      <div className="grid gap-1.5 leading-none">
        <label
          htmlFor={uniqueId}
          className={clsx(
            'text-sm font-semibold text-slate-700 [.theme-liquid-glass_&]:text-white/90',
            disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
            labelClassName
          )}
        >
          {resolvedLabel}
        </label>
        {resolvedDescription && (
          <p
            id={`${uniqueId}-description`}
            className={clsx('text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60', descriptionClassName)}
          >
            {resolvedDescription}
          </p>
        )}
      </div>
    </div>
  );
};
